import * as React from 'react';
import { ChevronDown, ChevronUp, Trash2, Upload } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  cn,
} from '@aimmo/ui';
import {
  MEDIA_ROLES,
  MEDIA_ROLE_LABELS,
  type MediaRole,
  type PropertyInput,
} from '@aimmo/contracts';
import { api, assertOk } from '../lib/api.js';

type MediaItem = NonNullable<PropertyInput['media']>[number];

export interface MediaFieldProps {
  value: unknown;
  onChange: (value: MediaItem[]) => void;
  disabled: boolean;
}

function asMedia(value: unknown): MediaItem[] {
  return Array.isArray(value) ? (value as MediaItem[]) : [];
}

/**
 * Property media: upload, order, role and caption.
 *
 * The bytes never touch the API. The browser asks for a presigned PUT, uploads
 * straight to the bucket and then sends back only the object key, which is what
 * makes a slow 20 MB upload the browser's problem and not the event loop's
 * (docs/architecture/06-async-and-jobs.md).
 *
 * Order is the array order; the API renumbers `position` on save.
 */
export function MediaField({ value, onChange, disabled }: MediaFieldProps) {
  const items = asMedia(value);
  const [uploading, setUploading] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const input = React.useRef<HTMLInputElement | null>(null);

  const update = (index: number, patch: Partial<MediaItem>) => {
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    onChange(next);
  };

  /** Exactly one cover: choosing a new one demotes the previous. */
  const setRole = (index: number, role: MediaRole) => {
    onChange(
      items.map((item, i) => {
        if (i === index) return { ...item, role };
        return role === 'cover' && item.role === 'cover' ? { ...item, role: 'photo' } : item;
      }),
    );
  };

  const upload = async (files: FileList) => {
    setError(null);
    const uploaded: MediaItem[] = [];

    for (const file of Array.from(files)) {
      setUploading((count) => count + 1);
      try {
        const ticket = assertOk(
          await api.properties.uploadTicket({
            body: { fileName: file.name, contentType: file.type, sizeBytes: file.size },
          }),
        ).body;

        const response = await fetch(ticket.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'content-type': file.type },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        uploaded.push({
          path: ticket.path,
          // The first image of an empty gallery is the cover: it is what every
          // list and export shows, and leaving it unset means showing nothing.
          role: items.length === 0 && uploaded.length === 0 ? 'cover' : 'photo',
          fileName: file.name,
          contentType: file.type,
          sizeBytes: file.size,
          caption: null,
        });
      } catch {
        setError(`Caricamento di ${file.name} non riuscito.`);
      } finally {
        setUploading((count) => count - 1);
      }
    }

    if (uploaded.length > 0) onChange([...items, ...uploaded]);
  };

  return (
    <div className="grid gap-2">
      {items.length === 0 && (
        <p className="rounded-control border border-dashed border-border px-3 py-4 text-secondary text-muted">
          Nessun file. Foto, planimetrie e documenti dell’immobile.
        </p>
      )}

      {items.map((item, index) => (
        <div
          key={item.path}
          className={cn(
            'grid grid-cols-[auto_1fr_auto] items-center gap-2 rounded-control border border-border p-2',
            'sm:grid-cols-[auto_2fr_1fr_1fr_auto]',
          )}
        >
          <div className="flex flex-col">
            <button
              type="button"
              aria-label="Sposta su"
              disabled={disabled || index === 0}
              className="rounded-control px-1 text-muted hover:text-ink disabled:opacity-40"
              onClick={() => move(index, -1)}
            >
              <ChevronUp className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Sposta giù"
              disabled={disabled || index === items.length - 1}
              className="rounded-control px-1 text-muted hover:text-ink disabled:opacity-40"
              onClick={() => move(index, 1)}
            >
              <ChevronDown className="size-4" />
            </button>
          </div>

          <span className="truncate text-body text-ink" title={item.fileName}>
            {item.fileName}
          </span>

          <Select
            value={item.role}
            disabled={disabled}
            onValueChange={(role) => setRole(index, role as MediaRole)}
          >
            <SelectTrigger aria-label={`Ruolo di ${item.fileName}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MEDIA_ROLES.map((role) => (
                <SelectItem key={role} value={role}>
                  {MEDIA_ROLE_LABELS[role]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            aria-label={`Didascalia di ${item.fileName}`}
            placeholder="Didascalia"
            value={item.caption ?? ''}
            disabled={disabled}
            onChange={(event) => update(index, { caption: event.target.value || null })}
          />

          <button
            type="button"
            aria-label="Rimuovi file"
            disabled={disabled}
            className="rounded-control p-2 text-muted hover:bg-surface-sunken hover:text-danger"
            onClick={() => onChange(items.filter((_, i) => i !== index))}
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      ))}

      {error && <p className="text-secondary text-danger">{error}</p>}

      <div>
        <input
          ref={input}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/avif,application/pdf"
          className="hidden"
          onChange={(event) => {
            if (event.target.files) void upload(event.target.files);
            // Same file twice in a row must still fire a change event.
            event.target.value = '';
          }}
        />
        {/* Full width and drawn in the accent: this is the primary action of
            the section, and as a ghost button it read as a caption (U2). */}
        <Button
          type="button"
          variant="subtle"
          size="lg"
          className="h-auto w-full flex-col gap-1 border-2 border-dashed border-accent py-6"
          disabled={disabled || uploading > 0}
          onClick={() => input.current?.click()}
        >
          <span className="flex items-center gap-2 text-body font-medium">
            {uploading > 0 ? <Spinner className="text-current" /> : <Upload />}
            {uploading > 0 ? `Caricamento (${uploading})…` : 'Carica file'}
          </span>
          <span className="text-secondary text-muted">JPEG, PNG, WebP, AVIF o PDF</span>
        </Button>
      </div>
    </div>
  );
}
